// src/pages/admin/AdminClinicProfile.jsx
import { useState, useEffect, useMemo } from "react";
import { Button, Card, DataStateHandler, Dropdown, showError, showSuccess } from "@common";
import {
  useClinicDetail,
  useClinics,
  useNetsuiteAccounts,
  useNetsuiteSubsidiaries,
  useUpdateClinic,
} from "@api/hooks";

export default function AdminClinicProfile({ fixedClinicCode = null, initialClinicCode = null }) {
  const trimmedFixed = typeof fixedClinicCode === "string" ? fixedClinicCode.trim() : "";
  const lockClinic = trimmedFixed.length > 0;

  const [selectedCode, setSelectedCode] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => emptyClinicForm());

  const { mutate: updateClinic, isPending: isSaving } = useUpdateClinic();

  // ------- hooks -------
  const {
    data: clinicsData,
    isPending: clinicsLoading,
    error: clinicsError,
  } = useClinics({ include_inactive: true }, { enablePolling: false });

  const clinicCodes = useMemo(() => {
    if (lockClinic) {
      return [trimmedFixed];
    }
    const raw = clinicsData?.results ?? clinicsData ?? [];
    const list = Array.isArray(raw) ? raw : [];
    const codes = list.map((c) => c.clinic).filter(Boolean);
    return [...new Set(codes)].sort((a, b) => a.localeCompare(b));
  }, [lockClinic, trimmedFixed, clinicsData]);

  useEffect(() => {
    if (lockClinic) {
      return;
    }
    if (!clinicCodes.length) {
      if (selectedCode) setSelectedCode("");
      return;
    }
    if (!selectedCode || !clinicCodes.includes(selectedCode)) {
      const preferred =
        initialClinicCode && clinicCodes.includes(initialClinicCode)
          ? initialClinicCode
          : clinicCodes[0];
      setSelectedCode(preferred);
    }
  }, [lockClinic, clinicCodes, selectedCode, initialClinicCode]);

  const code = lockClinic ? trimmedFixed : selectedCode;

  const {
    data,
    isPending: detailLoading,
    error: detailError,
  } = useClinicDetail(code, {
    includeInactive: true,
    enablePolling: false,
  });

  // clinic-detail payload: { clinic, related_data, ... }
  const clinic = data?.clinic;

  useEffect(() => {
    setForm(createClinicFormState(clinic));
    setIsEditing(false);
  }, [clinic]);

  // Resolve NetSuite subsidiary name from the clinic's netsuite_subsidiary_id
  const subsidiaryId = clinic?.netsuite_subsidiary_id ?? null;
  const subsidiaryQuery = useNetsuiteSubsidiaries(
    { id: subsidiaryId },
    { enabled: !!subsidiaryId, enablePolling: false },
  );
  const subsidiaryName = useMemo(() => {
    if (!subsidiaryId) return "";
    const raw = subsidiaryQuery.data;
    const list = raw?.items ?? raw?.results ?? (Array.isArray(raw) ? raw : []);
    const match = list.find((s) => String(s?.id ?? s?.ID) === String(subsidiaryId));
    return match?.name ?? match?.fullname ?? match?.fullName ?? "";
  }, [subsidiaryQuery.data, subsidiaryId]);

  const subsidiaryDisplay = useMemo(() => {
    if (!subsidiaryId) return "";
    if (subsidiaryName) return subsidiaryName;
    if (subsidiaryQuery.isLoading || subsidiaryQuery.isPending) return "Loading…";
    return "";
  }, [subsidiaryId, subsidiaryName, subsidiaryQuery.isLoading, subsidiaryQuery.isPending]);

  // Resolve NetSuite account name from netsuite_cash_in_hand
  const cashInHandId = clinic?.netsuite_cash_in_hand ?? null;
  const cashInHandQuery = useNetsuiteAccounts(
    { id: cashInHandId },
    { enabled: !!cashInHandId, enablePolling: false },
  );
  const cashInHandName = useMemo(() => {
    if (!cashInHandId) return "";
    const raw = cashInHandQuery.data;
    const list = raw?.items ?? raw?.results ?? (Array.isArray(raw) ? raw : []);
    const match = list.find((a) => String(a?.id ?? a?.ID) === String(cashInHandId));
    return match?.displaynamewithhierarchy ?? match?.fullname ?? match?.name ?? "";
  }, [cashInHandQuery.data, cashInHandId]);
  const cashInHandDisplay = useMemo(() => {
    if (!cashInHandId) return "";
    if (cashInHandName) return cashInHandName;
    if (cashInHandQuery.isLoading || cashInHandQuery.isPending) return "Loading…";
    return "";
  }, [cashInHandId, cashInHandName, cashInHandQuery.isLoading, cashInHandQuery.isPending]);

  const waitingForSelection = !lockClinic && clinicCodes.length > 0 && !code;
  const busy = waitingForSelection || (!!code && detailLoading);
  const listError = lockClinic ? null : clinicsError;
  const profileError = detailError;

  const clinicOptions = useMemo(() => {
    const raw = clinicsData?.results ?? clinicsData ?? [];
    const list = Array.isArray(raw) ? raw : [];
    const byCode = new Map(list.map((c) => [c.clinic, c]));
    return clinicCodes
      .map((c) => {
        const row = byCode.get(c);
        return { code: c, label: row?.name?.trim() ? `${row.name} (${c})` : c };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clinicsData, clinicCodes]);

  const clinicSelectOptions = useMemo(
    () => clinicOptions.map(({ code: optCode, label }) => ({ value: optCode, label })),
    [clinicOptions],
  );

  const rows = useMemo(() => {
    if (!clinic) return [];

    if (isEditing) {
      return [
        {
          label: "Clinic name",
          value: (
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
              placeholder="Clinic name"
            />
          ),
        },
        {
          label: "Address",
          value: (
            <textarea
              value={form.address}
              onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
              rows={3}
              placeholder="Street address"
            />
          ),
        },
        {
          label: "City",
          value: (
            <input
              type="text"
              value={form.city}
              onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
              placeholder="City"
            />
          ),
        },
        {
          label: "Province",
          value: (
            <input
              type="text"
              value={form.province}
              onChange={(e) => setForm((prev) => ({ ...prev, province: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
              placeholder="Province (e.g. ON)"
            />
          ),
        },
        {
          label: "Postal / ZIP code",
          value: (
            <input
              type="text"
              value={form.zip_code}
              onChange={(e) => setForm((prev) => ({ ...prev, zip_code: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
              placeholder="Postal / ZIP"
            />
          ),
        },
        {
          label: "LOC number",
          value: (
            <input
              type="text"
              value={form.loc_number}
              onChange={(e) => setForm((prev) => ({ ...prev, loc_number: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
              placeholder="Line of credit account number"
            />
          ),
        },
        {
          label: "LOC limit",
          value: (
            <input
              type="number"
              step="0.01"
              value={form.loc_limit}
              onChange={(e) => setForm((prev) => ({ ...prev, loc_limit: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
              placeholder="Line of credit limit"
            />
          ),
        },
        {
          label: "Status",
          accent: true,
          value: (
            <StatusEditor
              statusType={form.status_type}
              clinicIds={form.status_clinic_ids}
              onChange={({ type, clinicIds: ids }) =>
                setForm((prev) => ({ ...prev, status_type: type, status_clinic_ids: ids }))
              }
              clinicOptions={clinicSelectOptions}
              currentClinicCode={clinic.clinic}
            />
          ),
        },
        {
          label: "Date purchased",
          accent: true,
          value: (
            <input
              type="date"
              value={form.date_purchased}
              onChange={(e) => setForm((prev) => ({ ...prev, date_purchased: e.target.value }))}
              className="w-full rounded-lg border border-palette-gray-border px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
            />
          ),
        },
      ];
    }

    return [
      { label: "Clinic name", value: clinicDisplayName(clinic) },
      {
        label: "NetSuite subsidiary",
        value: subsidiaryId
          ? subsidiaryDisplay
            ? `${subsidiaryDisplay} (${subsidiaryId})`
            : String(subsidiaryId)
          : subsidiaryDisplay,
      },
      {
        label: "NetSuite cash in hand",
        value: cashInHandId
          ? cashInHandDisplay
            ? `${cashInHandDisplay} (${cashInHandId})`
            : String(cashInHandId)
          : cashInHandDisplay,
      },
      { label: "Address", value: formatAddress(clinic) },
      { label: "LOC number", value: stringOrEmpty(clinic.loc_number) },
      { label: "LOC limit", value: formatCurrency(clinic.loc_limit) },
      { label: "Phone / contact", value: firstNonEmptyString(clinic, CLINIC_PHONE_FIELD_KEYS) },
      { label: "Working hours", value: firstNonEmptyString(clinic, CLINIC_HOURS_FIELD_KEYS) },
      {
        label: "Holidays / non-working days",
        value: firstNonEmptyString(clinic, CLINIC_HOLIDAYS_FIELD_KEYS),
      },
      {
        label: "Date purchased",
        accent: true,
        value: renderDateValue(clinic.date_purchased),
      },
      {
        label: "Status",
        accent: true,
        value: renderStatusValue(clinic.status),
      },
    ];
  }, [
    clinic,
    isEditing,
    form,
    clinicSelectOptions,
    subsidiaryDisplay,
    subsidiaryId,
    cashInHandDisplay,
    cashInHandId,
  ]);

  const isDirty = useMemo(() => {
    if (!clinic) return false;
    return JSON.stringify(createClinicFormState(clinic)) !== JSON.stringify(form);
  }, [clinic, form]);

  function handleCancelEdit() {
    setForm(createClinicFormState(clinic));
    setIsEditing(false);
  }

  function handleSave() {
    if (!clinic?.id) return;
    const payload = buildClinicPatchPayload(form);
    updateClinic(
      { id: clinic.id, data: payload },
      {
        onSuccess: () => {
          showSuccess("Clinic profile updated.");
          setIsEditing(false);
        },
        onError: (err) => {
          const detail =
            err?.response?.data?.status?.[0] ||
            err?.response?.data?.detail ||
            "Failed to update clinic profile.";
          showError(detail);
        },
      },
    );
  }

  const headerTitle = clinic ? clinicDisplayName(clinic) : "Clinic profile";
  const subCode =
    clinic?.clinic && !lockClinic ? (
      <span className="text-sm font-normal text-palette-gray-secondary ml-2">
        ({clinic.clinic})
      </span>
    ) : null;

  const profileBody = (
    <DataStateHandler
      isLoading={lockClinic ? busy : clinicsLoading || busy}
      error={listError || profileError}
      isEmpty={!lockClinic && !clinicsLoading && !listError && !clinicCodes.length}
      emptyMessage="No clinics returned from the server."
      loadingMessage="Loading clinic profile…"
      showLoadingSpinner={true}
    >
      <div className="space-y-5 max-w-4xl mx-auto px-4 sm:px-6 py-4">
        {rows.map((r) => (
          <ProfileFieldRow
            key={r.label}
            label={r.label}
            value={r.value}
            editMode={isEditing}
            accent={r.accent}
          />
        ))}
      </div>
    </DataStateHandler>
  );

  // Admin: centered card; clinic dashboard: standard Card header
  if (!lockClinic) {
    return (
      <div className="mx-auto w-full max-w-5xl">
        <Card className="mt-2" bodyClassName="p-0">
          <div className="border-b border-palette-gray-divider px-4 py-4">
            <div className="mx-auto flex max-w-4xl flex-col items-center gap-3 sm:relative sm:min-h-[48px] sm:flex-row sm:items-center sm:justify-center">
              <div className="w-full max-w-[13rem] shrink-0 self-start sm:absolute sm:left-0 sm:top-1/2 sm:z-10 sm:-translate-y-1/2">
                <Dropdown
                  ariaLabel="Select clinic"
                  value={selectedCode}
                  onChange={setSelectedCode}
                  options={clinicSelectOptions}
                  placeholder={clinicCodes.length ? undefined : "No clinics"}
                  disabled={!clinicCodes.length}
                  className="[&_select]:h-10 [&_select]:px-2 [&_select]:py-2 [&_select]:text-sm [&_select]:focus:ring-palette-indigo-label"
                />
              </div>
              <h3 className="m-0 w-full text-center text-base font-semibold text-palette-gray-primary flex flex-wrap items-baseline justify-center gap-x-1 sm:px-60">
                <span>{headerTitle}</span>
                {subCode}
              </h3>
              {clinic ? (
                <div className="w-full sm:absolute sm:right-0 sm:top-1/2 sm:z-10 sm:flex sm:w-auto sm:-translate-y-1/2 sm:justify-end">
                  {isEditing ? (
                    <div className="flex gap-2 self-end">
                      <Button
                        size="sm"
                        variant="gray"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant={isDirty && !isSaving ? "success" : "gray"}
                        onClick={handleSave}
                        disabled={!isDirty || isSaving}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                      Edit
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          {profileBody}
        </Card>
      </div>
    );
  }

  return (
    <Card
      className="mt-2"
      title={
        <span className="flex flex-wrap items-baseline gap-x-1">
          <span>{headerTitle}</span>
          {subCode}
        </span>
      }
      right={<span className="text-sm text-palette-gray-badge tabular-nums">{trimmedFixed}</span>}
    >
      {clinic ? (
        <div className="mb-4 flex justify-end gap-2">
          {isEditing ? (
            <>
              <Button size="sm" variant="gray" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant={isDirty && !isSaving ? "success" : "gray"}
                onClick={handleSave}
                disabled={!isDirty || isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </div>
      ) : null}
      {profileBody}
    </Card>
  );
}

// ------- helpers (same pattern as Forecasts.jsx currency(), etc.) -------

function ProfileFieldRow({ label, value, editMode = false, accent = false }) {
  const labelCls = `text-sm font-medium pt-2.5 ${accent ? "text-palette-indigo-badge" : "text-palette-gray-badge"}`;

  if (isReactNode(value)) {
    return (
      <div className="grid gap-2 md:grid-cols-[minmax(200px,260px)_1fr] md:items-start">
        <div className={labelCls}>{label}</div>
        {editMode ? (
          <div className="py-1">{value}</div>
        ) : (
          <div className="flex min-h-[48px] items-center rounded-xl border border-palette-gray-divider bg-white px-4 py-3 text-[15px] text-palette-gray-primary">
            {value}
          </div>
        )}
      </div>
    );
  }

  const trimmed = value != null ? String(value).trim() : "";
  const display = trimmed === "" ? "—" : trimmed;
  const multiline = display.includes("\n");

  return (
    <div className="grid gap-2 md:grid-cols-[minmax(200px,260px)_1fr] md:items-start">
      <div className={labelCls}>{label}</div>
      <div
        className={`rounded-xl border border-palette-gray-divider bg-white px-4 py-3 text-[15px] text-palette-gray-primary min-h-[48px] ${
          multiline ? "whitespace-pre-wrap" : ""
        }`}
      >
        {display}
      </div>
    </div>
  );
}

function emptyClinicForm() {
  return {
    name: "",
    date_purchased: "",
    status_type: "",
    status_clinic_ids: [],
    address: "",
    city: "",
    province: "",
    zip_code: "",
    loc_number: "",
    loc_limit: "",
  };
}

function createClinicFormState(clinic) {
  if (!clinic) return emptyClinicForm();
  const { type: status_type, clinicIds: status_clinic_ids } = parseStatus(clinic.status);
  return {
    name: stringOrEmpty(clinic.name),
    date_purchased: stringOrEmpty(clinic.date_purchased),
    status_type,
    status_clinic_ids,
    address: stringOrEmpty(clinic.address),
    city: stringOrEmpty(clinic.city),
    province: stringOrEmpty(clinic.province),
    zip_code: stringOrEmpty(clinic.zip_code),
    loc_number: stringOrEmpty(clinic.loc_number),
    loc_limit: stringOrEmpty(clinic.loc_limit),
  };
}

function buildClinicPatchPayload(form) {
  return {
    name: normalizeNullableString(form.name),
    date_purchased: normalizeNullableString(form.date_purchased),
    status: buildStatusString(form.status_type, form.status_clinic_ids),
    address: normalizeNullableString(form.address),
    city: normalizeNullableString(form.city),
    province: normalizeNullableString(form.province),
    zip_code: normalizeNullableString(form.zip_code),
    loc_number: normalizeNullableString(form.loc_number),
    loc_limit: normalizeNullableString(form.loc_limit),
    // `netsuite_bank_balance` is deliberately ABSENT. The profile no longer shows
    // or edits it, and this is a PATCH — omitting the key leaves the stored value
    // alone, whereas echoing the loaded copy back would let an unrelated profile
    // save silently rewrite it (and blank it outright if the column ever held a
    // non-array). The live account registry is `BankAccount`, which superseded
    // this legacy JSON column.
  };
}

function normalizeNullableString(value) {
  const trimmed = stringOrEmpty(value).trim();
  return trimmed === "" ? null : trimmed;
}

function stringOrEmpty(value) {
  return value == null ? "" : String(value);
}

function isReactNode(value) {
  return typeof value === "object" && value !== null;
}

function parseStatus(status) {
  if (!status) return { type: "", clinicIds: [] };
  const lower = status.trim().toLowerCase();
  if (lower === "active") return { type: "active", clinicIds: [] };
  if (lower === "inactive") return { type: "inactive", clinicIds: [] };
  if (lower === "sold") return { type: "sold", clinicIds: [] };
  if (lower === "closed") return { type: "closed", clinicIds: [] };
  try {
    const parsed = JSON.parse(status);
    if (typeof parsed === "object" && String(parsed?.status ?? "").toLowerCase() === "merged") {
      return {
        type: "merged",
        clinicIds: Array.isArray(parsed.clinic_ids) ? parsed.clinic_ids : [],
      };
    }
  } catch {}
  return { type: "", clinicIds: [] };
}

function buildStatusString(type, clinicIds) {
  if (!type) return null;
  if (type === "active") return "active";
  if (type === "inactive" || type === "sold" || type === "closed") return type;
  if (type === "merged") return JSON.stringify({ status: "merged", clinic_ids: clinicIds });
  return null;
}

function StatusEditor({
  statusType,
  clinicIds,
  onChange,
  clinicOptions = [],
  currentClinicCode = "",
}) {
  const mergeOptions = clinicOptions.filter((o) => o.value !== currentClinicCode);
  const selectedClinic = clinicIds[0] ?? "";

  function handleTypeChange(newType) {
    onChange({ type: newType, clinicIds: [] });
  }

  function handleClinicSelect(code) {
    onChange({ type: "merged", clinicIds: code ? [code] : [] });
  }

  return (
    <div className="space-y-3">
      <select
        value={statusType}
        onChange={(e) => handleTypeChange(e.target.value)}
        className="w-full rounded-lg border border-palette-gray-border bg-white px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
      >
        <option value="">— None —</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="sold">Sold</option>
        <option value="closed">Closed</option>
        <option value="merged">Merged</option>
      </select>

      {statusType === "merged" && (
        <select
          value={selectedClinic}
          onChange={(e) => handleClinicSelect(e.target.value)}
          className="w-full rounded-lg border border-palette-indigo-hover bg-palette-indigo-soft px-3 py-2 text-sm text-palette-gray-primary focus:border-palette-indigo-label focus:outline-none focus:ring-1 focus:ring-palette-indigo-label"
        >
          <option value="">— Select clinic to merge with —</option>
          {mergeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function renderDateValue(date) {
  if (!date) return "";
  return (
    <span className="font-medium text-palette-indigo-heading">
      {new Date(date + "T00:00:00").toLocaleDateString("en-CA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })}
    </span>
  );
}

function renderStatusValue(status) {
  if (!status) return "";
  const lower = status.trim().toLowerCase();
  if (lower === "active") return <StatusPill color="emerald" label="Active" />;
  if (lower === "inactive") return <StatusPill color="amber" label="Inactive" />;
  if (lower === "sold") return <StatusPill color="rose" label="Sold" />;
  if (lower === "closed") return <StatusPill color="gray" label="Closed" />;
  try {
    const parsed = JSON.parse(status);
    if (typeof parsed === "object" && String(parsed?.status ?? "").toLowerCase() === "merged") {
      const ids = Array.isArray(parsed.clinic_ids) ? parsed.clinic_ids : [];
      return (
        <StatusPill color="indigo" label={ids.length ? `Merged → ${ids.join(", ")}` : "Merged"} />
      );
    }
  } catch {}
  return status;
}

function StatusPill({ color, label }) {
  const styles = {
    emerald:
      "bg-palette-emerald-muted text-palette-emerald-hover border border-palette-emerald-border",
    rose: "bg-palette-rose-muted text-palette-rose-heading border border-palette-rose-border",
    amber: "bg-palette-amber-muted text-palette-amber-heading border border-palette-amber-border",
    gray: "bg-palette-gray-subtle text-palette-gray-badge border border-palette-gray-border",
    indigo:
      "bg-palette-indigo-muted text-palette-indigo-heading border border-palette-indigo-border",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${styles[color] ?? styles.gray}`}
    >
      {label}
    </span>
  );
}

function formatCurrency(value) {
  if (value == null || String(value).trim() === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatAddress(c) {
  if (!c) return "";
  const line1 = c.address?.trim() || "";
  const cityPart = [c.city, c.province].filter(Boolean).join(", ").trim();
  const zip = c.zip_code?.trim() || "";
  const parts = [line1, cityPart, zip].filter(Boolean);
  return parts.join("\n");
}

function firstNonEmptyString(obj, keys) {
  if (!obj) return "";
  for (const key of keys) {
    const v = obj[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

const CLINIC_NAME_FIELD_KEYS = ["name", "name_production", "name_netsuite_cust"];
const CLINIC_PHONE_FIELD_KEYS = [
  "phone",
  "contact_phone",
  "phone_number",
  "main_phone",
  "clinic_phone",
];
const CLINIC_HOURS_FIELD_KEYS = ["working_hours", "hours_of_operation", "office_hours"];
const CLINIC_HOLIDAYS_FIELD_KEYS = [
  "holidays",
  "non_working_days",
  "closed_days",
  "holiday_schedule",
];

function clinicDisplayName(c) {
  if (!c) return "";
  return firstNonEmptyString(c, CLINIC_NAME_FIELD_KEYS) || c.clinic || "";
}
